import React, { createContext, useContext, useEffect, useState, useRef, useCallback } from 'react';
import { sanitizeInternalPath } from '@/utils/url';
import { useNavigate } from 'react-router-dom';
import { Session, User, AuthChangeEvent } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { identifyUser, resetUser, trackEvent } from '@/lib/posthog';
import { trackPixelEvent } from '@/lib/meta-pixel';
import { debugLog } from '@/lib/debug-logger';

// =============================================================================
// TYPES
// =============================================================================

type UserRole = 'admin' | 'business_owner' | 'staff';
type LayoutTheme = 'minimal' | 'quantum' | 'warp' | 'emoji';

type UserProfile = {
  id: string;
  first_name: string | null;
  last_name: string | null;
  avatar_url: string | null;
  role: UserRole;
  layout_theme?: LayoutTheme;
  created_at: string;
  updated_at: string;
};

export type DeleteAccountError =
  | { code: 'UPCOMING_BOOKINGS'; count: number }
  | { code: 'PENDING_PAYMENTS'; count: number }
  | { code: 'STRIPE_CANCEL_FAILED' }
  | { code: 'ALREADY_IN_PROGRESS' }
  | { code: 'UNKNOWN'; message: string };

type AuthContextType = {
  user: User | null;
  profile: UserProfile | null;
  session: Session | null;
  loading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (email: string, password: string, userData: Partial<UserProfile>) => Promise<void>;
  signOut: () => Promise<void>;
  updateProfile: (updates: Partial<UserProfile>) => Promise<void>;
  refreshProfile: () => Promise<void>;
  hasRole: (role: UserRole) => boolean;
  layoutTheme: LayoutTheme;
  setLayoutTheme: (theme: LayoutTheme) => void;
  deleteAccount: () => Promise<void>;
};

// =============================================================================
// CONTEXT
// =============================================================================

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// =============================================================================
// PROVIDER
// =============================================================================

export function AuthProvider({ children }: { children: React.ReactNode }) {
  // Core auth state
  const [user, setUser] = useState<User | null>(null);
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [layoutTheme, setLayoutTheme] = useState<LayoutTheme>('minimal');

  // Navigation
  const navigate = useNavigate();

  // Refs for deduplication and cleanup
  const mountedRef = useRef(true);
  const initializedRef = useRef(false);
  const profileFetchedRef = useRef(false);
  const sessionRef = useRef<Session | null>(null);

  // ---------------------------------------------------------------------------
  // PROFILE FETCHING (non-blocking helper)
  // ---------------------------------------------------------------------------
  const fetchProfile = useCallback(async (userId: string): Promise<UserProfile | null> => {
    debugLog.auth('fetchProfile START - userId:', userId);
    try {
      debugLog.auth('fetchProfile - querying profiles table');
      const { data, error } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        debugLog.warn('[Auth] fetchProfile - error:', error.message);
        return null;
      }

      debugLog.auth('fetchProfile - success, hasData:', !!data);

      // Apply layout theme from profile
      if (data?.layout_theme) {
        debugLog.auth('fetchProfile - applying theme from profile:', data.layout_theme);
        setLayoutTheme(data.layout_theme as LayoutTheme);
      } else {
        debugLog.auth('fetchProfile - applying saved theme from localStorage');
        const savedTheme = localStorage.getItem('userSelectedLayout') as LayoutTheme;
        if (savedTheme) setLayoutTheme(savedTheme);
      }

      debugLog.auth('fetchProfile END - returning profile');
      return data as UserProfile;
    } catch (err) {
      debugLog.warn('[Auth] fetchProfile - exception:', err);
      return null;
    }
  }, []);

  // ---------------------------------------------------------------------------
  // ASYNC PROFILE LOAD (fire-and-forget, updates state when done)
  // ---------------------------------------------------------------------------
  // Track which user ID we're currently fetching for
  const currentUserIdRef = useRef<string | null>(null);

  const loadProfileAsync = useCallback((userId: string, userEmail?: string) => {
    debugLog.auth('loadProfileAsync called - userId:', userId, 'profileFetchedRef:', profileFetchedRef.current, 'currentUserIdRef:', currentUserIdRef.current);
    // Skip if already fetched for THIS user
    if (profileFetchedRef.current && currentUserIdRef.current === userId) {
      debugLog.auth('loadProfileAsync - already fetched for this user, skipping');
      return;
    }
    
    debugLog.auth('loadProfileAsync - marking as fetching for userId:', userId);
    // Mark as fetching for this specific user
    profileFetchedRef.current = true;
    currentUserIdRef.current = userId;

    debugLog.auth('loadProfileAsync - calling fetchProfile');
    fetchProfile(userId)
      .then((profileData) => {
        debugLog.auth('loadProfileAsync - fetchProfile completed, profileData:', !!profileData);
        // CRITICAL: Verify we're still loading for the same user
        // Prevents race condition if user switched during async fetch
        if (!mountedRef.current) {
          debugLog.auth('loadProfileAsync - component unmounted, ignoring result');
          return;
        }
        if (currentUserIdRef.current !== userId) {
          debugLog.auth('loadProfileAsync - stale user (currentUserIdRef:', currentUserIdRef.current, 'userId:', userId, '), ignoring');
          return;
        }

        debugLog.auth('loadProfileAsync - setting profile');
        setProfile(profileData);

        // PostHog identification
        if (profileData) {
          debugLog.auth('loadProfileAsync - identifying user in PostHog');
          identifyUser(userId, {
            email: userEmail,
            name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
            role: profileData.role,
            layout_theme: profileData.layout_theme,
            created_at: profileData.created_at,
          });
        }
        debugLog.auth('loadProfileAsync - complete');
      })
      .catch((err) => {
        debugLog.warn('[Auth] loadProfileAsync - fetchProfile failed:', err);
      });
  }, [fetchProfile]);

  // ---------------------------------------------------------------------------
  // AUTH INITIALIZATION & SUBSCRIPTION
  // ---------------------------------------------------------------------------
  useEffect(() => {
    debugLog.auth('useEffect START - initializedRef:', initializedRef.current);
    mountedRef.current = true;

    // Prevent double initialization (React StrictMode)
    // But still check for session changes if already initialized (e.g., after external OAuth)
    if (initializedRef.current) {
      debugLog.auth('Already initialized, checking for session changes from external auth');
      // Check if session appeared after initialization (external OAuth completed)
      supabase.auth.getSession().then(({ data: { session: currentSession } }) => {
        if (!mountedRef.current) return;

        // If we have a session now but didn't before, hydrate it
        if (currentSession && !sessionRef.current) {
          debugLog.auth('External auth detected - session appeared after initialization');
          sessionRef.current = currentSession;
          setSession(currentSession);
          setUser(currentSession.user);
          if (currentSession.user) {
            loadProfileAsync(currentSession.user.id, currentSession.user.email);
          }
        }
        setLoading(false);
      });
      return;
    }
    debugLog.auth('First initialization - marking initializedRef = true');
    initializedRef.current = true;

    // -------------------------------------------------------------------------
    // 1. INITIAL SESSION RESOLUTION (via getSession)
    // -------------------------------------------------------------------------
    const initializeSession = async () => {
      debugLog.auth('initializeSession START');
      try {
        debugLog.auth('Calling supabase.auth.getSession()');
        const { data: { session: initialSession }, error } = await supabase.auth.getSession();

        if (error) {
          console.error('[Auth] getSession error:', error.message);
        }

        debugLog.auth('getSession complete - hasSession:', !!initialSession, 'hasUser:', !!initialSession?.user);

        if (!mountedRef.current) {
          debugLog.auth('Component unmounted during getSession, returning early');
          return;
        }

        // Set session and user state
        sessionRef.current = initialSession;
        setSession(initialSession);
        setUser(initialSession?.user ?? null);
        debugLog.auth('Session and user state updated');

        // CRITICAL: Unblock UI immediately after session resolution
        debugLog.auth('Calling setLoading(false)');
        setLoading(false);

        // Load profile async (non-blocking)
        if (initialSession?.user) {
          debugLog.auth('Has user - calling loadProfileAsync for userId:', initialSession.user.id);
          loadProfileAsync(initialSession.user.id, initialSession.user.email);
        } else {
          // No session - apply saved theme
          debugLog.auth('No user - applying saved theme');
          const savedTheme = localStorage.getItem('userSelectedLayout') as LayoutTheme;
          if (savedTheme) setLayoutTheme(savedTheme);
        }
        debugLog.auth('initializeSession END');
      } catch (err) {
        console.error('[Auth] Session initialization failed:', err);
        if (mountedRef.current) {
          debugLog.auth('Error recovery - calling setLoading(false)');
          setLoading(false);
        }
      }
    };

    debugLog.auth('About to call initializeSession()');
    initializeSession();

    // -------------------------------------------------------------------------
    // 2. AUTH STATE CHANGE SUBSCRIPTION
    // -------------------------------------------------------------------------
    debugLog.auth('Setting up onAuthStateChange subscription');
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (event: AuthChangeEvent, currentSession: Session | null) => {
        debugLog.auth('onAuthStateChange fired - event:', event, 'hasSession:', !!currentSession, 'hasUser:', !!currentSession?.user);
        debugLog.auth('Current dedupe flags - profileFetchedRef:', profileFetchedRef.current, 'currentUserIdRef:', currentUserIdRef.current, 'sessionRef:', !!sessionRef.current);
        
        if (!mountedRef.current) {
          debugLog.auth('Component unmounted, ignoring event:', event);
          return;
        }

        switch (event) {
          case 'INITIAL_SESSION':
            debugLog.auth('INITIAL_SESSION - skipping (handled by getSession)');
            // SKIP: We handle initial session via getSession() above
            break;

          case 'SIGNED_IN':
            debugLog.auth('SIGNED_IN - checking if new session');
            // Only process if we don't already have this session (use ref for fresh value)
            if (!sessionRef.current || sessionRef.current.access_token !== currentSession?.access_token) {
              debugLog.auth('SIGNED_IN - new session detected, updating state');
              sessionRef.current = currentSession;
              setSession(currentSession);
              setUser(currentSession?.user ?? null);

              if (currentSession?.user) {
                // Check if this is a DIFFERENT user than before
                const isNewUser = currentUserIdRef.current !== currentSession.user.id;
                debugLog.auth('SIGNED_IN - isNewUser:', isNewUser, 'userId:', currentSession.user.id);

                // Fire signup_completed for new account creation
                // Detect new accounts by checking if created_at is within the last 2 minutes
                const createdAt = new Date(currentSession.user.created_at).getTime();
                const isNewAccount = Date.now() - createdAt < 120_000;
                if (isNewAccount) {
                  const startedAt = sessionStorage.getItem('figgy_signup_started_at');
                  const timeToSignup = startedAt
                    ? Math.round((Date.now() - Number(startedAt)) / 1000)
                    : undefined;
                  const provider = currentSession.user.app_metadata?.provider || 'email';
                  trackEvent('signup_completed', {
                    signup_method: provider,
                    time_to_signup_seconds: timeToSignup,
                  });
                  trackPixelEvent('CompleteRegistration');
                  sessionStorage.removeItem('figgy_signup_started_at');
                }

                if (isNewUser) {
                  debugLog.auth('SIGNED_IN - new user, resetting profile state');
                  // Reset profile state for new user
                  profileFetchedRef.current = false;
                  currentUserIdRef.current = null;
                  setProfile(null);
                }
                
                // Load profile if not already fetched for THIS user
                if (!profileFetchedRef.current) {
                  debugLog.auth('SIGNED_IN - calling loadProfileAsync for userId:', currentSession.user.id);
                  loadProfileAsync(currentSession.user.id, currentSession.user.email);
                } else {
                  debugLog.auth('SIGNED_IN - profile already fetched, skipping');
                }
              }
            } else {
              debugLog.auth('SIGNED_IN - duplicate session, skipping');
            }
            break;

          case 'SIGNED_OUT':
            debugLog.auth('SIGNED_OUT - clearing all auth state');
            sessionRef.current = null;
            setSession(null);
            setUser(null);
            setProfile(null);
            profileFetchedRef.current = false;
            currentUserIdRef.current = null;

            // Detach analytics identity here so every sign-out path is covered
            // (including callers that hit supabase.auth.signOut() directly and
            // bypass the signOut() method); otherwise a later identify() on a
            // shared browser can group two users' events.
            resetUser();

            // Restore saved theme preference
            const savedTheme = localStorage.getItem('userSelectedLayout') as LayoutTheme;
            if (savedTheme) setLayoutTheme(savedTheme);
            break;

          case 'TOKEN_REFRESHED':
            debugLog.auth('TOKEN_REFRESHED - updating session');
            // Just update session, don't refetch profile
            if (currentSession) {
              sessionRef.current = currentSession;
              setSession(currentSession);
            }
            break;

          case 'USER_UPDATED':
            debugLog.auth('USER_UPDATED - updating user object');
            // Update user object
            if (currentSession?.user) {
              setUser(currentSession.user);
            }
            break;

          case 'PASSWORD_RECOVERY':
            debugLog.auth('PASSWORD_RECOVERY - ignoring');
            // Optional: handle password recovery flow
            break;

          default:
            debugLog.auth('Unknown event:', event);
            break;
        }
      }
    );

    // -------------------------------------------------------------------------
    // 3. CROSS-TAB AUTH SYNC (localStorage events)
    // -------------------------------------------------------------------------
    const handleStorageEvent = async (e: StorageEvent) => {
      if (!mountedRef.current) return;

      const isSignOutEvent = e.key === 'auth_signout_event';
      const isAuthKey = e.key?.includes('-auth-token');

      if (isSignOutEvent) {
        debugLog.auth('Sign out detected in another tab');
        sessionRef.current = null;
        setSession(null);
        setUser(null);
        setProfile(null);
        profileFetchedRef.current = false;
        currentUserIdRef.current = null;
        navigate('/fancy-signin');
        return;
      }

      if (isAuthKey) {
        // Another tab may have signed in - refresh session
        try {
          const { data: { session: latestSession } } = await supabase.auth.getSession();
          if (!mountedRef.current) return;

          sessionRef.current = latestSession;
          setSession(latestSession);
          setUser(latestSession?.user ?? null);

          if (latestSession?.user) {
            // Check if this is a DIFFERENT user than we had before
            const isNewUser = currentUserIdRef.current !== latestSession.user.id;
            
            if (isNewUser) {
              // Reset profile state for new user
              profileFetchedRef.current = false;
              currentUserIdRef.current = null;
              setProfile(null);
            }
            
            // Load profile if not already fetched for THIS user
            if (!profileFetchedRef.current) {
              loadProfileAsync(latestSession.user.id, latestSession.user.email);
            }
          } else {
            // No session - clear everything
            setProfile(null);
            profileFetchedRef.current = false;
            currentUserIdRef.current = null;
          }
        } catch (err) {
          debugLog.warn('[Auth] Cross-tab sync error:', err);
        }
      }
    };

    window.addEventListener('storage', handleStorageEvent);

    // -------------------------------------------------------------------------
    // CLEANUP
    // -------------------------------------------------------------------------
    return () => {
      mountedRef.current = false;
      subscription.unsubscribe();
      window.removeEventListener('storage', handleStorageEvent);
    };
    // NOTE: `session` intentionally excluded - we access it via closure
    // Including it would cause subscription teardown/recreation on every session change
     
  }, [loadProfileAsync, navigate]);

  // ---------------------------------------------------------------------------
  // LAYOUT THEME HANDLER
  // ---------------------------------------------------------------------------
  const handleSetLayoutTheme = useCallback(async (theme: LayoutTheme) => {
    setLayoutTheme(theme);
    localStorage.setItem('userSelectedLayout', theme);

    if (user) {
      try {
        const { error } = await supabase
          .from('profiles')
          .update({ layout_theme: theme })
          .eq('id', user.id);
        
        if (error) {
          debugLog.warn('[Auth] Failed to save theme to profile:', error.message);
        }
      } catch (err) {
        debugLog.warn('[Auth] Theme save exception:', err);
      }
    }
  }, [user]);

  // ---------------------------------------------------------------------------
  // SIGN IN
  // ---------------------------------------------------------------------------
  const signIn = useCallback(async (email: string, password: string) => {
    try {
      setLoading(true);
      const { data, error } = await supabase.auth.signInWithPassword({ email, password });

      if (error) {
        toast.error(error.message);
        throw error;
      }

      if (!data.session || !data.user) {
        throw new Error('Sign in failed - no session returned');
      }

      // Update both state AND ref to prevent SIGNED_IN handler from re-processing
      sessionRef.current = data.session;
      setSession(data.session);
      setUser(data.user);

      // Fetch profile synchronously for navigation decision
      const profileData = await fetchProfile(data.user.id);
      setProfile(profileData);
      profileFetchedRef.current = true;
      currentUserIdRef.current = data.user.id;

      // Identify user in PostHog analytics
      if (profileData) {
        identifyUser(data.user.id, {
          email: data.user.email,
          name: `${profileData.first_name || ''} ${profileData.last_name || ''}`.trim(),
          role: profileData.role,
          layout_theme: profileData.layout_theme,
          created_at: profileData.created_at,
        });
      }

      // Determine redirect destination
      const params = new URLSearchParams(window.location.search);
      const rawNext = params.get('next');
      const safeNext = sanitizeInternalPath(rawNext);
      const defaultDestination = '/dashboard';

      // Check onboarding status. Use limit(1) instead of .single() so duplicate
      // business_profiles rows (legacy upsert bug) don't cause a false null.
      try {
        const { data: profileRows } = await supabase
          .from('business_profiles')
          .select('id')
          .eq('user_id', data.user.id)
          .order('created_at', { ascending: false })
          .limit(1);

        const businessProfile = profileRows?.[0] ?? null;

        if (!businessProfile) {
          // No business profile — needs onboarding
          navigate('/magic-onboarding-v2');
        } else {
          // Has business profile — check for services to confirm completion
          const { data: services } = await supabase
            .from('business_services')
            .select('id')
            .eq('business_id', businessProfile.id)
            .limit(1);

          if (services && services.length > 0) {
            navigate(safeNext || defaultDestination);
          } else {
            navigate('/magic-onboarding-v2');
          }
        }
      } catch (err) {
        debugLog.warn('[Auth] Onboarding check failed, proceeding to dashboard:', err);
        navigate(safeNext || defaultDestination);
      }

      toast.success('Signed in successfully!');
    } catch (error: any) {
      console.error('Sign in error:', error);
      toast.error(error.message || 'Sign in failed');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [fetchProfile, navigate]);

  // ---------------------------------------------------------------------------
  // SIGN UP
  // ---------------------------------------------------------------------------
  const signUp = useCallback(async (email: string, password: string, userData: Partial<UserProfile>) => {
    try {
      setLoading(true);

      const rawNext = new URLSearchParams(window.location.search).get('next');
      const safeNext = sanitizeInternalPath(rawNext);
      const nextParam = safeNext ? `?next=${encodeURIComponent(safeNext)}` : '';
      const redirectTo = `${window.location.origin}/auth/callback${nextParam}`;

      const savedTheme = localStorage.getItem('userSelectedLayout') as LayoutTheme;

      const { data, error } = await supabase.auth.signUp({
        email,
        password,
        options: {
          data: {
            name: userData.first_name,
            avatar_url: userData.avatar_url,
            layout_theme: savedTheme || 'minimal',
          },
          emailRedirectTo: redirectTo,
        },
      });

      if (error) {
        toast.error(error.message);
        throw error;
      }

      // Create profile if user was created
      if (data.user) {
        const { error: profileError } = await supabase.from('profiles').insert({
          id: data.user.id,
          first_name: userData.first_name,
          last_name: userData.last_name,
          avatar_url: userData.avatar_url,
          role: 'business_owner',
          layout_theme: savedTheme || 'minimal',
        });

        if (profileError) {
          console.error('Profile creation failed:', profileError);
          toast.error('Account created but profile setup failed. Try signing in again.');
        }
      }

      if (data.session === null) {
        toast.success('Account created! Check your email for the confirmation link.');
        navigate('/fancy-signin');
      } else {
        toast.success('Account created successfully!');
        navigate('/dashboard');
      }
    } catch (error: any) {
      console.error('Sign up error:', error);
      toast.error(error.message || 'Sign up failed');
      throw error;
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // SIGN OUT
  // ---------------------------------------------------------------------------
  const signOut = useCallback(async () => {
    try {
      setLoading(true);

      // Signal other tabs
      localStorage.setItem('auth_signout_event', JSON.stringify({ timestamp: Date.now() }));

      const { error } = await supabase.auth.signOut();

      if (error) {
        toast.error(error.message);
        throw error;
      }

      setUser(null);
      setProfile(null);
      setSession(null);
      profileFetchedRef.current = false;

      resetUser();

      // Cleanup signal
      setTimeout(() => localStorage.removeItem('auth_signout_event'), 1000);

      toast.success('Signed out successfully');
      navigate('/fancy-signin');
    } catch (error: any) {
      console.error('Sign out error:', error);
      toast.error(error.message || 'Sign out failed');
    } finally {
      setLoading(false);
    }
  }, [navigate]);

  // ---------------------------------------------------------------------------
  // UPDATE PROFILE
  // ---------------------------------------------------------------------------
  const updateProfile = useCallback(async (updates: Partial<UserProfile>) => {
    if (!user) return;

    try {
      setLoading(true);

      const { data, error } = await supabase
        .from('profiles')
        .update(updates)
        .eq('id', user.id)
        .select()
        .single();

      if (error) {
        toast.error(error.message);
        throw error;
      }

      setProfile(data as UserProfile);
      toast.success('Profile updated successfully');
    } catch (error: any) {
      console.error('Update profile error:', error);
      toast.error(error.message || 'Failed to update profile');
    } finally {
      setLoading(false);
    }
  }, [user]);

  // ---------------------------------------------------------------------------
  // REFRESH PROFILE
  // ---------------------------------------------------------------------------
  const refreshProfile = useCallback(async () => {
    if (!user) return;

    try {
      const profileData = await fetchProfile(user.id);
      setProfile(profileData);
    } catch (err) {
      debugLog.warn('[Auth] Profile refresh failed:', err);
    }
  }, [user, fetchProfile]);

  // ---------------------------------------------------------------------------
  // DELETE ACCOUNT
  // ---------------------------------------------------------------------------
  const deleteAccount = useCallback(async () => {
    if (!session) throw new Error('No active session');

    const { data, error } = await supabase.functions.invoke('delete-account', {
      body: { confirmation: 'DELETE' },
      headers: { Authorization: `Bearer ${session.access_token}` },
    });

    // supabase.functions.invoke() puts 4xx/5xx bodies in `error.context` (FunctionsHttpError),
    // not in `data`. Parse the body from the error when present so we can act on `code`.
    let body = data;
    if (error && !data) {
      try {
        body = await (error as any).context?.json?.();
      } catch {
        // context not parseable — fall through to UNKNOWN
      }
    }

    if (body?.code === 'UPCOMING_BOOKINGS') {
      throw { code: 'UPCOMING_BOOKINGS', count: body.count } satisfies DeleteAccountError;
    }
    if (body?.code === 'PENDING_PAYMENTS') {
      throw { code: 'PENDING_PAYMENTS', count: body.count } satisfies DeleteAccountError;
    }
    if (body?.code === 'STRIPE_CANCEL_FAILED') {
      throw { code: 'STRIPE_CANCEL_FAILED' } satisfies DeleteAccountError;
    }
    if (body?.alreadyInProgress) {
      throw { code: 'ALREADY_IN_PROGRESS' } satisfies DeleteAccountError;
    }
    if (error && !body?.deleted) {
      throw { code: 'UNKNOWN', message: body?.error ?? error.message } satisfies DeleteAccountError;
    }

    // Success — clear local state and redirect. Auth session is now invalid.
    setUser(null);
    setProfile(null);
    setSession(null);
    profileFetchedRef.current = false;
    resetUser();
    navigate('/');
  }, [session, navigate]);

  // ---------------------------------------------------------------------------
  // HAS ROLE
  // ---------------------------------------------------------------------------
  const hasRole = useCallback((role: UserRole): boolean => {
    return profile?.role === role;
  }, [profile]);

  // ---------------------------------------------------------------------------
  // RENDER
  // ---------------------------------------------------------------------------
  return (
    <AuthContext.Provider
      value={{
        user,
        profile,
        session,
        loading,
        signIn,
        signUp,
        signOut,
        updateProfile,
        refreshProfile,
        hasRole,
        layoutTheme,
        setLayoutTheme: handleSetLayoutTheme,
        deleteAccount,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
}

// =============================================================================
// HOOK
// =============================================================================

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
